import { RestClient } from "./restClient";
import RestResponse from "../models/client/RestResponse";
import RequestMethod from "../models/client/RequestMethod";
import { SearchRequest } from "@/models/search/SearchRequest";
import { SearchPagination } from "@/models/search/SearchPagination";
import { SearchResult } from "@/models/search/SearchResults";

export interface StoredSearch {
  id: string;
  userId: string;
  searchUrl: string;
  isFavorite: boolean;
  favoriteLinkId?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export interface FavoriteSearchesResponse {
  searches: StoredSearch[];
  displayOrder: string[];
}

export interface CreateSearchRequest {
  searchUrl: string;
}

export type SearchServiceType = {
  tenantSearch: (
    request: SearchRequest
  ) => RestResponse<SearchPagination<SearchResult>>;

  getMyRecentSearches: () => RestResponse<StoredSearch[]>;
  getMyFavoriteSearches: () => RestResponse<FavoriteSearchesResponse>;
  createSearch: (request: CreateSearchRequest) => RestResponse<StoredSearch>;
  favoriteSearch: (id: string) => RestResponse<StoredSearch>;
  unfavoriteSearch: (id: string) => RestResponse<StoredSearch>;
  deleteSearch: (id: string) => RestResponse<void>;
  deleteAllNonFavoriteSearches: () => RestResponse<void>;
};

const searchServices = (
  client: RestClient,
  path: string
): SearchServiceType => ({
  tenantSearch: async (request: SearchRequest) => {
    const requestBody = {
      searchQuery: request.searchTerm,
      entityTypes: request.entityTypes,
      sources: request.sources,
      filters: request.filters,
      sourceFilters: request.sourceFilters,
      pageNumber: request.pageNumber,
      pageSize: request.pageSize,
    };

    const response = await client.fetch<SearchPagination<SearchResult>>(
      RequestMethod.POST,
      path,
      requestBody
    );
    return response;
  },

  getMyRecentSearches: async () => {
    return await client.fetch<StoredSearch[]>(
      RequestMethod.GET,
      `${path}/recent`
    );
  },
  getMyFavoriteSearches: async () => {
    return await client.fetch<FavoriteSearchesResponse>(
      RequestMethod.GET,
      `${path}/favorite`
    );
  },

  createSearch: async (request: CreateSearchRequest) => {
    return await client.fetch<StoredSearch>(
      RequestMethod.POST,
      `${path}/create`,
      request
    );
  },

  favoriteSearch: async (id: string) => {
    return await client.fetch<StoredSearch>(
      RequestMethod.PUT,
      `${path}/${id}/favorite`
    );
  },

  unfavoriteSearch: async (id: string) => {
    return await client.fetch<StoredSearch>(
      RequestMethod.PUT,
      `${path}/${id}/unfavorite`
    );
  },

  deleteSearch: async (id: string) => {
    return await client.fetch<void>(RequestMethod.DELETE, `${path}/${id}`);
  },

  deleteAllNonFavoriteSearches: async () => {
    return await client.fetch<void>(
      RequestMethod.DELETE,
      `${path}/delete-non-favorite`
    );
  },
});

export default searchServices;
